import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Box,
  Grid,
  FormControl,
  Select,
  InputAdornment,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PriceField from './PriceField';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

function AddTray({ open, onClose, onAdd, editMode = false, trayToEdit = {}, setSuccess, setError, }) {
  const { user, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const defaultCurrency = farmData?.settings?.currency || '\u00A3';
  const defaultUnit = farmData?.settings?.measureUnit || 'cm';

  const inlineSelectSx = {
    fontSize: '0.85rem',
    opacity: 0.85,
    '&:hover': { opacity: 1 },
    '&.Mui-focused': { opacity: 1 },
    '& .MuiSelect-icon': { fontSize: 18 },
  };

  const renderInlineSelect = (value, onChange, options) => (
    <Select
      variant="standard"
      disableUnderline
      sx={inlineSelectSx}
      value={value}
      onChange={onChange}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  // Updated state with separate fields for length, width, stock, and their units,
  // plus a new field for priceCurrency.
  const [newTray, setNewTray] = useState({
    length: '',
    lengthUnit: defaultUnit,
    width: '',
    widthUnit: defaultUnit,
    brand: '',
    price: '',
    priceCurrency: defaultCurrency,
    stock: '',
    available: '',
    notes: '',
  });

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editMode && trayToEdit && trayToEdit.id) {
      const existingStock = Number(trayToEdit.stock) || 0;
      const existingAvailable = trayToEdit.available != null
        ? Number(trayToEdit.available) || 0
        : existingStock;
      const reserved = Math.max(existingStock - existingAvailable, 0);
      setNewTray({
        length: trayToEdit.length || '',
        lengthUnit: trayToEdit.lengthUnit || defaultUnit,
        width: trayToEdit.width || '',
        widthUnit: trayToEdit.widthUnit || defaultUnit,
        brand: trayToEdit.brand || '',
        price: trayToEdit.price || '',
        priceCurrency: trayToEdit.priceCurrency || defaultCurrency,
        stock: trayToEdit.stock || '',
        available: existingAvailable,
        reserved,
        notes: trayToEdit.notes || '',
      });
    } else {
      setNewTray({
        length: '',
        lengthUnit: defaultUnit,
        width: '',
        widthUnit: defaultUnit,
        brand: '',
        price: '',
        priceCurrency: defaultCurrency,
        stock: '',
        available: '',
        notes: '',
      });
    }

    setFormSubmitted(false);
    setTouched({});
  }, [open, editMode, trayToEdit, defaultCurrency, defaultUnit]);


  const handleSave = async () => {
    setFormSubmitted(true);
    if (!newTray.brand || !newTray.stock) {
      setError?.('Please fill in required fields: Name/Brand and Stock Number');
      return;
    }
    try {
      if (!user.uid) {
        setError?.('User not logged in.');
        return;
      }
      if (!activeFarmId) {
        setError?.('No active farm selected.');
        return;
      }

      const stockNumber = Number(newTray.stock) || 0;

      if (editMode && trayToEdit?.id) {
        const existingStock = Number(trayToEdit.stock) || 0;
        const existingAvailable = trayToEdit.available != null
          ? Number(trayToEdit.available) || 0
          : existingStock;
        const reserved = Math.max(existingStock - existingAvailable, 0);

        if (stockNumber < reserved) {
          setError?.(`Stock cannot be lower than the ${reserved} tray${reserved === 1 ? '' : 's'} currently in use.`);
          return;
        }

        const trayPayload = {
          ...newTray,
          stock: stockNumber,
          available: stockNumber - reserved,
        };
        delete trayPayload.reserved;

        await updateDoc(doc(db, 'farms', activeFarmId, 'trays', trayToEdit.id), trayPayload);
        setSuccess?.('Tray updated successfully.');
      } else {
        const trayPayload = {
          ...newTray,
          stock: stockNumber,
          available: stockNumber,
        };

        await addDoc(collection(db, 'farms', activeFarmId, 'trays'), trayPayload);
        setSuccess?.('Tray added successfully.')
      }
      if (onAdd) onAdd(newTray);
      handleCloseDialog();
    } catch (error) {
      setError?.('Error saving tray.');
    }
  };

  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const isError = (field) => {
    return !newTray[field] && (touched[field] || formSubmitted);
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown}>
      <DialogTitle>{editMode ? 'Edit Tray' : 'Add New Tray'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1} alignContent="center" justifyContent="space-between">
          <Grid item xs={12} sm={8}>
            <TextField
              label="Name / Brand"
              fullWidth
              required
              value={newTray.brand}
              onChange={(e) => setNewTray({ ...newTray, brand: e.target.value })}
              onBlur={() => handleBlur('brand')}
              error={isError('brand')}
              helperText={isError('brand') ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Stock Number"
              fullWidth
              required
              value={newTray.stock}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  setNewTray({ ...newTray, stock: val });
                }
              }}
              onBlur={() => handleBlur('stock')}
              error={isError('stock')}
              helperText={isError('stock') ? 'Required' : ''}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <PriceField
              label="Price Per Unit"
              fullWidth
              value={newTray.price}
              onChange={(formattedValue) =>
                setNewTray({ ...newTray, price: formattedValue })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {renderInlineSelect(
                      newTray.priceCurrency || defaultCurrency,
                      (e) =>
                        setNewTray({ ...newTray, priceCurrency: e.target.value }),
                      [
                        { value: '\u00A3', label: '\u00A3' },
                        { value: '$', label: '$' },
                        { value: '\u20AC', label: '\u20AC' },
                      ]
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: { xs: 1, sm: 0 },
              }}
            >
              <TextField
                margin="dense"
                label="Length"
                fullWidth
                value={newTray.length}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty string or only digits
                  if (/^\d*$/.test(val)) {
                    setNewTray({ ...newTray, length: val });
                  }
                }}
                onBlur={() => handleBlur('length')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {renderInlineSelect(
                        newTray.lengthUnit,
                        (e) =>
                          setNewTray({ ...newTray, lengthUnit: e.target.value }),
                        [
                          { value: 'cm', label: 'cm' },
                          { value: 'in', label: 'in' },
                        ]
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <ClearIcon fontSize="small" />
              <TextField
                margin="dense"
                label="Width"
                fullWidth
                value={newTray.width}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty string or only digits
                  if (/^\d*$/.test(val)) {
                    setNewTray({ ...newTray, width: val });
                  }
                }}
                onBlur={() => handleBlur('width')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {renderInlineSelect(
                        newTray.widthUnit,
                        (e) =>
                          setNewTray({ ...newTray, widthUnit: e.target.value }),
                        [
                          { value: 'cm', label: 'cm' },
                          { value: 'in', label: 'in' },
                        ]
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <TextField
          margin="dense"
          label="Notes"
          fullWidth
          multiline
          minRows={2}
          value={newTray.notes}
          onChange={(e) => setNewTray({ ...newTray, notes: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          {editMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddTray;



